import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, ChevronLeft, ChevronRight, Download, FileText, Loader2, Upload } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import s3Service from '@/services/s3Service.js';
import mammoth from 'mammoth';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import {
  isTemplateFillOnlineSupported,
  mimeTypeForFilledTemplate,
  replacePlaceholdersInPlainText,
  replacePlaceholdersInRtf,
} from '@/utils/templateFillSupport.js';
import { rtfStringToPlainText } from '@/utils/rtfToPlainText.js';
import { plainTextToDocxBlob } from '@/utils/plainTextToDocxBlob.js';
import { TEMPLATE_FIELD_POLICY } from '@/constants/templateWizardPolicy.js';

const LOG_NS = 'TemplateReviewWizard';

const ACTIVE_ANCHOR_ID = 'template-active-placeholder';

const DEFAULT_COPY = {
  title: 'Islamic Finance Template Review',
  subtitle:
    'Move through each field with Previous and Next, scroll the full document, then download your updated file (same format as your template when supported). Built for riba-free Islamic Finance and halal loans workflows.',
  stepUpload: 'Upload template',
  stepFill: 'Fill fields',
  stepReview: 'Review & download',
  uploadLabel: 'Select a template file',
  uploadHint: 'Supported: .txt, .md, .html, .json, .doc, .docx, .rtf',
  fieldsTitle: 'Current field',
  previewTitle: 'Document',
  previewHint: 'Scroll to read the full text. Filled values update as you type. The active field is highlighted.',
  previousField: 'Previous field',
  nextField: 'Next field',
  next: 'Next',
  back: 'Back',
  download: 'Download',
  processing: 'Processing file...',
  generating: 'Generating...',
  missingTemplate: 'Please upload a template file to continue.',
  downloadReady: 'Your updated file is ready to download.',
  fieldKey: 'Field name',
  fieldValueOptional: 'Value (optional)',
  fieldValueRequired: 'Value (required)',
  valuePlaceholderOptional: 'Leave default or replace with your text',
  valuePlaceholderRequired: 'Enter your replacement text',
  fieldsOptionalBadge: 'optional fields',
  fieldsRequiredBadge: 'fields must be replaced',
  fieldsHint:
    'Use Previous and Next to move through the document. Each value starts as the template token; change only what you need.',
  fieldsHintStrict:
    'Use Previous and Next to move through the document. Replace every placeholder with your own text before reviewing the file.',
  requireAllFieldsReplaced:
    'Replace every placeholder with new text before continuing. Fields still showing the default token are not finished.',
  noteNoClaims:
    'Note: Keep wording accurate for your institution and product. This tool supports drafting and internal review.',
  unsupportedFile:
    'This file type cannot be opened in the online template flow. Use DOC, DOCX, RTF, TXT, MD, HTML, or JSON.',
  docReadError: 'Could not read this .doc file. Save as .docx and try again.',
  rtfParseError: 'Could not read this RTF file. Check the file and try again.',
  secureSubmitTitle: 'Submit for review',
  secureSubmitDescription:
    'When you confirm below, your completed file is uploaded over an encrypted connection to secure storage (S3) for our team to review. You can still download a copy for your records.',
  secureSubmitCheckboxLabel:
    'I confirm this document is complete and accurate, and I authorize HalalCred to store it securely and review it.',
  secureSubmitButton: 'Confirm and send for review',
  secureSubmitting: 'Uploading to secure storage…',
  secureSubmitSuccess: 'Submitted successfully. Our team will review your document.',
  secureSubmitFailed: 'Upload failed. You can retry or use Download and contact support.',
  secureSubmitMissingConfig: 'Secure submission is not configured for this screen.',
};

function getFileExtension(filename) {
  if (!filename || typeof filename !== 'string') return '';
  const parts = filename.split('.');
  return parts.length >= 2 ? parts.pop().toLowerCase() : '';
}

function detectDelimiters(docxContent) {
  if (docxContent.includes('<<') && docxContent.includes('>>')) return { start: '<<', end: '>>' };
  if (docxContent.includes('«') && docxContent.includes('»')) return { start: '«', end: '»' };
  if (docxContent.includes('{{') && docxContent.includes('}}')) return { start: '{{', end: '}}' };
  if (docxContent.includes('[') && docxContent.includes(']')) return { start: '[', end: ']' };
  return { start: '{', end: '}' };
}

/** Same token shapes as segmentTemplateForDisplay; one pass preserves document order. */
const PLACEHOLDER_ORDER_RX = /<<([^<>]+)>>|«([^«»]+)»|\{\{([^{}]+)\}\}|\[([^\]]+)\]|\{([^{}]+)\}/g;

function innerKeyFromPlaceholderMatch(m) {
  return (m[1] || m[2] || m[3] || m[4] || m[5] || '').trim();
}

/** First literal placeholder token in the text for this inner key (for default input value). */
function firstRawPlaceholderForKey(text, innerKey) {
  const target = innerKey.trim().toLowerCase();
  if (!target) return `{${innerKey}}`;
  const rx = new RegExp(PLACEHOLDER_ORDER_RX.source, 'g');
  const haystack = String(text ?? '');
  let m;
  while ((m = rx.exec(haystack)) !== null) {
    if (innerKeyFromPlaceholderMatch(m).trim().toLowerCase() === target) {
      return m[0];
    }
  }
  return `{${innerKey.trim()}}`;
}

function buildPairsFromExtractedKeys(extractedKeys, defaultSourceText) {
  if (!extractedKeys.length) return [{ key: '', value: '', defaultToken: '' }];
  return extractedKeys.map((k) => {
    const defaultToken = firstRawPlaceholderForKey(defaultSourceText, k);
    return { key: k, value: defaultToken, defaultToken };
  });
}

/**
 * Placeholders in **first-appearance order** (reading left to right). Deduplicates by trimmed key.
 * For DOCX XML, scans tag-stripped text first (approximate reading order), then raw XML for any keys missed (e.g. split across runs).
 */
function extractPlaceholders(content, isDocxXml = false) {
  const ordered = [];
  const seen = new Set();

  const walk = (str) => {
    if (!str) return;
    const rx = new RegExp(PLACEHOLDER_ORDER_RX.source, 'g');
    let m;
    while ((m = rx.exec(str)) !== null) {
      const inner = innerKeyFromPlaceholderMatch(m);
      if (!inner || seen.has(inner)) continue;
      seen.add(inner);
      ordered.push(inner);
    }
  };

  const primary = isDocxXml ? String(content).replace(/<[^>]+>/g, '') : String(content ?? '');
  walk(primary);
  if (isDocxXml) {
    walk(String(content ?? ''));
  }
  return ordered;
}

/** Split plain text into alternating body text and placeholder tokens (for scroll + highlight). */
function segmentTemplateForDisplay(text) {
  const raw = String(text ?? '');
  const rx = new RegExp(PLACEHOLDER_ORDER_RX.source, 'g');
  const segments = [];
  let last = 0;
  let m;
  while ((m = rx.exec(raw)) !== null) {
    if (m.index > last) {
      segments.push({ type: 'text', value: raw.slice(last, m.index) });
    }
    const inner = innerKeyFromPlaceholderMatch(m);
    segments.push({ type: 'ph', raw: m[0], inner });
    last = rx.lastIndex;
  }
  if (last < raw.length) {
    segments.push({ type: 'text', value: raw.slice(last) });
  }
  return segments.length ? segments : [{ type: 'text', value: raw }];
}

function isPlaceholderStillDefault(value, defaultToken) {
  const v = String(value ?? '').trim();
  const d = String(defaultToken ?? '').trim();
  if (!d) return !v;
  return !v || v === d;
}

/**
 * @param {object} props
 * @param {Record<string, string>} [props.copy] Partial UI strings merged with defaults (per route / modal).
 * @param {'optional'|'requireAllReplaced'} [props.fieldPolicy] Use `TEMPLATE_FIELD_POLICY` from `@/constants/templateWizardPolicy.js`.
 * @param {boolean} [props.enableSecureSubmit] When true, step 3 offers confirm + upload to S3 via presigned URL (JWT storage API).
 * @param {string|number} [props.secureSubmitDocumentType] Storage step key / stepId for presign (e.g. `productDevelopment` for training uploads). Required when `enableSecureSubmit` is true.
 * @param {string} [props.secureSubmitPartnerId] Folder segment for object keys; defaults to `localStorage.userId` or `demo-user-id`.
 * @param {(uploadResult: object) => void} [props.onSecureSubmitted] Called after a successful secure upload.
 */
export default function TemplateReviewWizard({
  copy: copyOverrides = {},
  className = '',
  fieldPolicy = TEMPLATE_FIELD_POLICY.OPTIONAL,
  initialTemplate,
  showHeader = true,
  onError,
  onCompleted,
  enableSecureSubmit = false,
  secureSubmitDocumentType,
  secureSubmitPartnerId,
  onSecureSubmitted,
}) {
  const copy = { ...DEFAULT_COPY, ...copyOverrides };
  const isStrictPolicy = fieldPolicy === TEMPLATE_FIELD_POLICY.REQUIRE_ALL_REPLACED;
  const secureSubmitConfigured =
    Boolean(enableSecureSubmit) &&
    secureSubmitDocumentType !== null &&
    secureSubmitDocumentType !== undefined &&
    String(secureSubmitDocumentType).trim() !== '';
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [originalFileData, setOriginalFileData] = useState(null);
  /** Raw RTF source for export; plain preview stays in `templateContent`. */
  const [originalRtfSource, setOriginalRtfSource] = useState(null);
  const [pairs, setPairs] = useState([{ key: '', value: '' }]);
  const [generatedContent, setGeneratedContent] = useState('');
  const [generatedFileData, setGeneratedFileData] = useState(null);
  /** File extension for the last successful generate (handles RTF → DOCX fallback). */
  const [generatedDownloadExt, setGeneratedDownloadExt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeFillIndex, setActiveFillIndex] = useState(0);
  const [secureSubmitConfirmed, setSecureSubmitConfirmed] = useState(false);
  const [isSecureSubmitting, setIsSecureSubmitting] = useState(false);
  const [secureSubmitMessage, setSecureSubmitMessage] = useState('');
  const [secureSubmitError, setSecureSubmitError] = useState('');

  const abortControllerRef = useRef(null);
  const fileReaderRef = useRef(null);
  const lastInitialUrlRef = useRef(null);
  const documentScrollRef = useRef(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (fileReaderRef.current) {
        fileReaderRef.current.abort();
        fileReaderRef.current = null;
      }
    };
  }, []);

  const fillableFieldsRef = useRef([]);

  const fillableFields = useMemo(
    () =>
      pairs
        .map((p, originalIndex) => ({
          key: p.key,
          value: p.value,
          originalIndex,
          defaultToken: p.defaultToken,
        }))
        .filter((p) => p.key.trim()),
    [pairs]
  );

  fillableFieldsRef.current = fillableFields;

  const strictFieldsSatisfied = useMemo(() => {
    if (!isStrictPolicy) return true;
    if (fillableFields.length === 0) return true;
    return fillableFields.every((f) => {
      const def =
        pairs[f.originalIndex]?.defaultToken ?? firstRawPlaceholderForKey(templateContent, f.key);
      return !isPlaceholderStillDefault(f.value, def);
    });
  }, [isStrictPolicy, fillableFields, pairs, templateContent]);

  const currentField = fillableFields[activeFillIndex] ?? null;

  /** Live preview: same segments as the template, with entered values substituted for each placeholder. */
  const documentPreviewSegments = useMemo(() => {
    const valueByKeyLower = new Map();
    for (const p of pairs) {
      const k = p.key?.trim();
      if (!k) continue;
      valueByKeyLower.set(k.toLowerCase(), p.value ?? '');
    }
    return segmentTemplateForDisplay(templateContent).map((seg) => {
      if (seg.type !== 'ph') return seg;
      const lk = seg.inner.trim().toLowerCase();
      const rawVal = valueByKeyLower.get(lk);
      const trimmed = rawVal != null ? String(rawVal).trim() : '';
      return { ...seg, resolvedDisplay: trimmed ? trimmed : null };
    });
  }, [templateContent, pairs]);

  useEffect(() => {
    if (fillableFields.length === 0) {
      setActiveFillIndex(0);
      return;
    }
    setActiveFillIndex((i) => Math.min(Math.max(0, i), fillableFields.length - 1));
  }, [fillableFields.length]);

  useEffect(() => {
    if (step !== 1) return;
    const id = ACTIVE_ANCHOR_ID;
    const t = window.setTimeout(() => {
      const root = documentScrollRef.current;
      const el = root?.querySelector(`#${id}`) ?? document.getElementById(id);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    return () => window.clearTimeout(t);
  }, [step, activeFillIndex, currentField?.key, templateContent]);

  const handleFail = (message, err) => {
    setError(message);
    console.error(`[${LOG_NS}] ${message}`, err);
    if (typeof onError === 'function') onError(message, err);
  };

  const handleTextUpload = (file, signal) =>
    new Promise((resolve, reject) => {
      if (signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));
      if (fileReaderRef.current) fileReaderRef.current.abort();

      const reader = new FileReader();
      fileReaderRef.current = reader;
      reader.onload = (event) => {
        if (signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));
        const content = event.target.result;
        setTemplateContent(content);
        setFileType('text');
        setOriginalFileData(null);
        const extracted = extractPlaceholders(content, false);
        setPairs(buildPairsFromExtractedKeys(extracted, content));
        fileReaderRef.current = null;
        resolve();
      };
      reader.onerror = () => {
        fileReaderRef.current = null;
        reject(new Error('Failed to read file'));
      };
      reader.onabort = () => {
        fileReaderRef.current = null;
        reject(new DOMException('Aborted', 'AbortError'));
      };
      reader.readAsText(file, 'UTF-8');
    });

  const handleDocxUpload = async (file, signal) => {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const arrayBuffer = await file.arrayBuffer();
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    setOriginalFileData(arrayBuffer);

    const result = await mammoth.extractRawText({ arrayBuffer });
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    setTemplateContent(result.value);
    setFileType('docx');

    const zip = new PizZip(arrayBuffer);
    if (!zip.files['word/document.xml']) {
      throw new Error('Invalid DOCX: missing word/document.xml');
    }
    const docxContent = zip.files['word/document.xml'].asText();
    const extracted = extractPlaceholders(docxContent, true);
    setPairs(buildPairsFromExtractedKeys(extracted, result.value));
  };

  const handleDocUpload = async (file, signal) => {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const arrayBuffer = await file.arrayBuffer();
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const result = await mammoth.extractRawText({ arrayBuffer });
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    setTemplateContent(result.value);
    setFileType('doc');
    setOriginalFileData(arrayBuffer);
    const extracted = extractPlaceholders(result.value, false);
    setPairs(buildPairsFromExtractedKeys(extracted, result.value));
  };

  const handleRtfUpload = async (file, signal) => {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const rtfString = await file.text();
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const plain = await rtfStringToPlainText(rtfString);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    setTemplateContent(plain);
    setFileType('rtf');
    setOriginalFileData(null);
    setOriginalRtfSource(rtfString);
    const extracted = extractPlaceholders(plain, false);
    setPairs(buildPairsFromExtractedKeys(extracted, plain));
  };

  const processFile = async (file, { autoAdvanceToFill = true } = {}) => {
    if (!file) return;
    const name = file.name || '';
    if (!isTemplateFillOnlineSupported(name)) {
      handleFail(copy.unsupportedFile, new Error('Unsupported extension'));
      return;
    }

    setError('');
    setIsLoading(true);
    setFileName(name);
    setGeneratedContent('');
    setGeneratedFileData(null);
    setGeneratedDownloadExt('');
    setActiveFillIndex(0);
    setOriginalRtfSource(null);
    setSecureSubmitConfirmed(false);
    setSecureSubmitMessage('');
    setSecureSubmitError('');
    setIsSecureSubmitting(false);

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const ext = getFileExtension(name);
      console.info(`[${LOG_NS}] Processing template`, { name, ext, size: file.size });
      if (ext === 'docx') await handleDocxUpload(file, signal);
      else if (ext === 'doc') await handleDocUpload(file, signal);
      else if (ext === 'rtf') await handleRtfUpload(file, signal);
      else await handleTextUpload(file, signal);

      if (autoAdvanceToFill) setStep(1);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      const ext = getFileExtension(name);
      if (ext === 'doc') handleFail(copy.docReadError, err);
      else if (ext === 'rtf') handleFail(copy.rtfParseError, err);
      else handleFail('We could not process the template.', err);
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file, { autoAdvanceToFill: true });
  };

  useEffect(() => {
    const url = initialTemplate?.url;
    const name = initialTemplate?.name;
    if (!url || !name) return;
    if (lastInitialUrlRef.current === url) return;
    lastInitialUrlRef.current = url;

    (async () => {
      try {
        console.info(`[${LOG_NS}] Loading template from URL`, { url, name });
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`Failed to load template (${res.status})`);
        }
        const blob = await res.blob();
        const file = new File([blob], name, { type: blob.type || 'application/octet-stream' });
        await processFile(file, { autoAdvanceToFill: true });
      } catch (err) {
        handleFail('We could not load this template. Please download it and upload from your device.', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTemplate?.url, initialTemplate?.name]);

  const updatePair = (idx, field, value) => {
    setPairs((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const generateDocxWithDocxtemplater = async (signal) => {
    const zip = new PizZip(originalFileData);
    const docxContent = zip.files['word/document.xml'].asText();
    const delimiters = detectDelimiters(docxContent);

    const data = {};
    pairs.forEach(({ key, value }) => {
      if (!key?.trim() || !value?.trim()) return;
      let cleanKey = key.trim().replace(/^[«{[\<]+/, '').replace(/[»}\]\>]+$/, '').trim();
      if (cleanKey) data[cleanKey] = value;
    });

    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters,
      nullGetter: (part) => {
        const keyName = part.module || part.value || '';
        return delimiters.start + keyName + delimiters.end;
      },
    });

    doc.setData(data);
    doc.render();
    const outputZip = doc.getZip();
    const blob = outputZip.generate({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      compression: 'DEFLATE',
    });

    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    setGeneratedFileData(blob);
    const previewResult = await mammoth.extractRawText({ arrayBuffer: await blob.arrayBuffer() });
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    setGeneratedContent(previewResult.value);
  };

  const handleGenerate = async () => {
    setError('');
    if (!templateContent) {
      handleFail(copy.missingTemplate, new Error('Missing template content'));
      return;
    }

    if (isStrictPolicy && fillableFields.length > 0) {
      const unresolved = fillableFields.filter((f) => {
        const def =
          pairs[f.originalIndex]?.defaultToken ?? firstRawPlaceholderForKey(templateContent, f.key);
        return isPlaceholderStillDefault(f.value, def);
      });
      if (unresolved.length > 0) {
        handleFail(copy.requireAllFieldsReplaced, new Error('Strict template policy'));
        return;
      }
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsLoading(true);
    setSecureSubmitConfirmed(false);
    setSecureSubmitMessage('');
    setSecureSubmitError('');
    try {
      console.info(`[${LOG_NS}] Generating output`, { fileType, fileName });
      if (fileType === 'docx' && originalFileData) {
        await generateDocxWithDocxtemplater(signal);
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        setGeneratedDownloadExt('docx');
      } else if (fileType === 'rtf' && typeof originalRtfSource === 'string') {
        const filledRtf = replacePlaceholdersInRtf(originalRtfSource, pairs);
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const blob = new Blob([filledRtf], { type: 'application/rtf' });
        setGeneratedFileData(blob);
        setGeneratedContent(await rtfStringToPlainText(filledRtf));
        setGeneratedDownloadExt('rtf');
      } else if (fileType === 'rtf') {
        const filled = replacePlaceholdersInPlainText(templateContent, pairs);
        const blob = await plainTextToDocxBlob(filled);
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        setGeneratedFileData(blob);
        setGeneratedContent(filled);
        setGeneratedDownloadExt('docx');
      } else if (fileType === 'text') {
        const filled = replacePlaceholdersInPlainText(templateContent, pairs);
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const ext = getFileExtension(fileName) || 'txt';
        const blob = new Blob([filled], { type: mimeTypeForFilledTemplate(ext) });
        setGeneratedFileData(blob);
        setGeneratedContent(filled);
        setGeneratedDownloadExt(ext);
      } else if (fileType === 'doc') {
        const filled = replacePlaceholdersInPlainText(templateContent, pairs);
        const blob = await plainTextToDocxBlob(filled);
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        setGeneratedFileData(blob);
        setGeneratedContent(filled);
        setGeneratedDownloadExt('docx');
      } else {
        const filled = replacePlaceholdersInPlainText(templateContent, pairs);
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const blob = new Blob([filled], { type: 'text/plain;charset=UTF-8' });
        setGeneratedFileData(blob);
        setGeneratedContent(filled);
        setGeneratedDownloadExt('txt');
      }

      setStep(2);
      if (typeof onCompleted === 'function') onCompleted();
    } catch (err) {
      if (err?.name === 'AbortError') return;
      handleFail('We could not generate the document. Please try again.', err);
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  };

  const getGeneratedExportMeta = () => {
    if (!generatedFileData && !generatedContent) return null;
    const originalName = fileName || 'template';
    const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
    const srcExt = getFileExtension(originalName);
    const outExt =
      generatedDownloadExt ||
      (fileType === 'docx' || fileType === 'doc' ? 'docx' : fileType === 'rtf' ? 'rtf' : srcExt || 'txt');

    let blob;
    const downloadName = `${nameWithoutExt}_updated.${outExt}`;
    if (generatedFileData) {
      blob = generatedFileData;
    } else {
      blob = new Blob([generatedContent], { type: mimeTypeForFilledTemplate(outExt) });
    }
    return { blob, downloadName, outExt };
  };

  const handleDownload = () => {
    try {
      const meta = getGeneratedExportMeta();
      if (!meta) {
        handleFail('Please generate the document before downloading.', new Error('Missing generated output'));
        return;
      }
      const { blob, downloadName } = meta;

      console.info(`[${LOG_NS}] Download`, { downloadName });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      handleFail('Download failed. Please try again.', err);
    }
  };

  const handleSecureSubmit = async () => {
    setSecureSubmitError('');
    setSecureSubmitMessage('');
    if (!secureSubmitConfirmed) return;
    if (!secureSubmitConfigured) {
      setSecureSubmitError(copy.secureSubmitMissingConfig);
      return;
    }
    const meta = getGeneratedExportMeta();
    if (!meta) {
      handleFail('Please generate the document before submitting.', new Error('Missing generated output'));
      return;
    }

    setIsSecureSubmitting(true);
    try {
      const { blob, downloadName } = meta;
      const mime = blob.type || mimeTypeForFilledTemplate(meta.outExt);
      const file = new File([blob], downloadName, { type: mime });
      const pid =
        (typeof secureSubmitPartnerId === 'string' && secureSubmitPartnerId.trim()) ||
        (typeof localStorage !== 'undefined' && localStorage.getItem('userId')) ||
        'demo-user-id';

      console.info(`[${LOG_NS}] Secure submit upload`, { downloadName, documentType: secureSubmitDocumentType });
      const result = await s3Service.uploadFile(file, pid, secureSubmitDocumentType);
      setSecureSubmitMessage(copy.secureSubmitSuccess);
      if (typeof onSecureSubmitted === 'function') onSecureSubmitted(result);
    } catch (err) {
      const msg = err?.message || String(err);
      setSecureSubmitError(
        /jwt|token|sign in|401/i.test(msg)
          ? `${copy.secureSubmitFailed} Sign in may be required for secure storage.`
          : copy.secureSubmitFailed
      );
      console.error(`[${LOG_NS}] Secure submit failed`, err);
      if (typeof onError === 'function') onError(copy.secureSubmitFailed, err);
    } finally {
      setIsSecureSubmitting(false);
    }
  };

  const goPrevField = () => {
    setActiveFillIndex((i) => Math.max(0, i - 1));
  };

  const goNextField = () => {
    setActiveFillIndex((i) => {
      const n = fillableFieldsRef.current?.length ?? 0;
      if (n <= 0) return 0;
      return Math.min(n - 1, i + 1);
    });
  };

  const canSubmitGenerate = Boolean(templateContent) && !isLoading && strictFieldsSatisfied;
  const steps = [
    { label: copy.stepUpload },
    { label: copy.stepFill },
    { label: copy.stepReview },
  ];

  let anchorPlaced = false;
  const activeKeyNorm = (currentField?.key || '').trim().toLowerCase();

  const currentFieldDefaultToken =
    currentField != null
      ? pairs[currentField.originalIndex]?.defaultToken ??
        firstRawPlaceholderForKey(templateContent, currentField.key)
      : '';

  const currentFieldUnresolvedStrict =
    isStrictPolicy &&
    currentField != null &&
    isPlaceholderStillDefault(currentField.value, currentFieldDefaultToken);

  return (
    <div className={className}>
      {showHeader && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{copy.title}</h1>
          <p className="text-gray-600">{copy.subtitle}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {steps.map((s, idx) => {
          const isActive = idx === step;
          const isDone = idx < step;
          return (
            <Badge
              key={s.label}
              variant="outline"
              className={[
                'px-3 py-1',
                isActive ? 'border-emerald-500 text-emerald-700 bg-emerald-50' : '',
                isDone ? 'border-emerald-200 text-emerald-800' : '',
              ].join(' ')}
            >
              {idx + 1}. {s.label}
            </Badge>
          );
        })}
        {step > 0 && fillableFields.length > 0 && (
          <Badge variant="outline" className="ml-auto">
            {fillableFields.length}{' '}
            {isStrictPolicy ? copy.fieldsRequiredBadge : copy.fieldsOptionalBadge}
          </Badge>
        )}
      </div>

      {error && (
        <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-900">{error}</p>
          </div>
        </div>
      )}

      {step === 1 ? (
        <div className="flex flex-col lg:flex-row gap-6 items-stretch">
          <Card className="lg:w-[340px] lg:flex-shrink-0 lg:sticky lg:top-4 lg:self-start">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="w-5 h-5" />
                {copy.fieldsTitle}
              </CardTitle>
              <CardDescription>{isStrictPolicy ? copy.fieldsHintStrict : copy.fieldsHint}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {fillableFields.length > 0 && currentField ? (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={goPrevField}
                      disabled={activeFillIndex <= 0 || isLoading}
                      aria-label={copy.previousField}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <span className="text-sm font-medium text-gray-700 text-center flex-1">
                      Field {activeFillIndex + 1} of {fillableFields.length}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={goNextField}
                      disabled={activeFillIndex >= fillableFields.length - 1 || isLoading}
                      aria-label={copy.nextField}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                  <div>
                    <Label className="text-xs">{copy.fieldKey}</Label>
                    <Input value={currentField.key} readOnly className="mt-1 bg-gray-50 font-mono text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 flex items-center gap-1">
                      {isStrictPolicy ? copy.fieldValueRequired : copy.fieldValueOptional}
                      {currentFieldUnresolvedStrict ? (
                        <span className="text-red-600" aria-hidden>
                          *
                        </span>
                      ) : null}
                    </Label>
                    <Input
                      value={currentField.value}
                      onChange={(e) => updatePair(currentField.originalIndex, 'value', e.target.value)}
                      placeholder={
                        isStrictPolicy ? copy.valuePlaceholderRequired : copy.valuePlaceholderOptional
                      }
                      className="mt-1"
                      autoFocus
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-600">
                  No placeholders were found. You can still generate an updated file from this text, or add tokens like{' '}
                  <span className="font-mono">{'{FIELD_NAME}'}</span> in your template.
                </p>
              )}

              <p className="text-xs text-gray-500">{copy.noteNoClaims}</p>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" onClick={() => setStep(0)} disabled={isLoading}>
                  {copy.back}
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={!canSubmitGenerate}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {copy.generating}
                    </>
                  ) : (
                    copy.stepReview
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="flex-1 min-w-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{copy.previewTitle}</CardTitle>
              <CardDescription>{copy.previewHint}</CardDescription>
            </CardHeader>
            <CardContent>
              {templateContent ? (
                <div
                  ref={documentScrollRef}
                  className="max-h-[min(70vh,640px)] overflow-y-auto rounded-md border border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-800 whitespace-pre-wrap font-sans"
                >
                  {documentPreviewSegments.map((seg, idx) => {
                    if (seg.type === 'text') {
                      return <span key={`t-${idx}`}>{seg.value}</span>;
                    }
                    const isActivePh = seg.inner.trim().toLowerCase() === activeKeyNorm;
                    const giveAnchor = isActivePh && !anchorPlaced;
                    if (giveAnchor) anchorPlaced = true;
                    const displayText = seg.resolvedDisplay ?? seg.raw;
                    const isFilled = Boolean(seg.resolvedDisplay);
                    return (
                      <mark
                        key={`p-${idx}`}
                        id={giveAnchor ? ACTIVE_ANCHOR_ID : undefined}
                        className={
                          isActivePh
                            ? 'rounded-sm px-0.5 ring-2 ring-emerald-500 ring-offset-1 bg-emerald-100 text-gray-900'
                            : isFilled
                              ? 'rounded-sm px-0.5 bg-emerald-50 text-gray-900 ring-1 ring-emerald-200'
                              : 'rounded-sm px-0.5 bg-amber-100 text-gray-900'
                        }
                      >
                        {displayText}
                      </mark>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Upload a template to see the document.</p>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {step === 0 ? copy.stepUpload : copy.stepReview}
              </CardTitle>
              <CardDescription>{step === 0 ? copy.uploadHint : copy.downloadReady}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {step === 0 && (
                <div>
                  <Label htmlFor="template-upload">{copy.uploadLabel}</Label>
                  <Input
                    id="template-upload"
                    type="file"
                    accept=".txt,.md,.html,.json,.doc,.docx,.rtf"
                    onChange={handleFileUpload}
                    className="mt-2"
                    disabled={isLoading}
                  />
                  {isLoading && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{copy.processing}</span>
                    </div>
                  )}
                  {fileName && (
                    <p className="text-sm text-gray-600 mt-2">
                      <span className="font-medium">Selected:</span> {fileName}
                    </p>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <p className="text-sm text-emerald-800">{copy.downloadReady}</p>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={handleDownload} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Download className="w-4 h-4 mr-2" />
                      {copy.download}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSecureSubmitConfirmed(false);
                        setSecureSubmitMessage('');
                        setSecureSubmitError('');
                        setStep(1);
                      }}
                      disabled={isLoading || isSecureSubmitting}
                    >
                      {copy.back}
                    </Button>
                  </div>

                  {enableSecureSubmit && (
                    <div className="rounded-lg border border-emerald-200/80 bg-emerald-50/40 p-4 space-y-3">
                      <div>
                        <h4 className="text-sm font-semibold text-[#1a4a1a]">{copy.secureSubmitTitle}</h4>
                        <p className="text-xs text-slate-600 mt-1">{copy.secureSubmitDescription}</p>
                      </div>
                      {!secureSubmitConfigured ? (
                        <p className="text-xs text-amber-800">{copy.secureSubmitMissingConfig}</p>
                      ) : (
                        <>
                          <label
                            htmlFor="template-wizard-secure-submit"
                            className="flex items-start gap-3 cursor-pointer"
                          >
                            <Checkbox
                              id="template-wizard-secure-submit"
                              checked={secureSubmitConfirmed}
                              onCheckedChange={(v) => setSecureSubmitConfirmed(v === true)}
                              disabled={isSecureSubmitting}
                              className="mt-0.5"
                            />
                            <span className="text-sm text-slate-800 leading-snug">{copy.secureSubmitCheckboxLabel}</span>
                          </label>
                          <Button
                            type="button"
                            onClick={handleSecureSubmit}
                            disabled={!secureSubmitConfirmed || isSecureSubmitting}
                            className="bg-[#1a4a1a] hover:bg-[#143d14] text-white"
                          >
                            {isSecureSubmitting ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {copy.secureSubmitting}
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                {copy.secureSubmitButton}
                              </>
                            )}
                          </Button>
                        </>
                      )}
                      {secureSubmitMessage ? (
                        <p className="text-sm text-emerald-800 font-medium">{secureSubmitMessage}</p>
                      ) : null}
                      {secureSubmitError ? (
                        <p className="text-sm text-red-700">{secureSubmitError}</p>
                      ) : null}
                    </div>
                  )}
                </div>
              )}

              {step === 0 && (
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => {
                      if (!templateContent) {
                        handleFail(copy.missingTemplate, new Error('No template uploaded'));
                        return;
                      }
                      setActiveFillIndex(0);
                      setStep(1);
                    }}
                    disabled={!templateContent || isLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {copy.next}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{copy.previewTitle}</CardTitle>
              <CardDescription>{copy.previewHint}</CardDescription>
            </CardHeader>
            <CardContent>
              {templateContent ? (
                <div className="max-h-[min(60vh,560px)] overflow-y-auto rounded-md border bg-gray-50 p-4 text-sm whitespace-pre-wrap">
                  {generatedContent || templateContent}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>Upload a template to start.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
